import { useEffect, useState, useCallback } from "react";
import { X, ExternalLink, BarChart3 } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Compare Pricing — small utility button that opens an apples-to-apples
 * comparison modal for a product. Renders NOTHING if no admin-managed
 * comparison data exists for the given SKU (auto-hides).
 *
 * Design: clinical, restrained, no red/green gaming of prices, no winner badges.
 */

const OXBLOOD = "#4A0E17";
const GOLD    = "#C9A24B";

export default function PriceComparisonButton({ sku }) {
  const [data, setData] = useState(null);
  const [state, setState] = useState("idle"); // idle | loading | ready | empty
  const [open, setOpen] = useState(false);

  // Probe on mount so button auto-hides when no data exists.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: res } = await api.get(`/price-comparisons/by-sku/${encodeURIComponent(sku)}`);
        if (!cancelled) { setData(res); setState("ready"); }
      } catch {
        if (!cancelled) { setState("empty"); }
      }
    })();
    return () => { cancelled = true; };
  }, [sku]);

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") close(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, close]);

  if (state !== "ready" || !data) return null;

  return (
    <>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        className="w-full inline-flex items-center justify-center rounded-sm font-mono uppercase tracking-[0.22em] font-bold text-[12.5px] py-2.5 gap-2 transition-colors shadow-sm"
        style={{
          color: OXBLOOD,
          background: `${OXBLOOD}0A`,
          borderColor: `${OXBLOOD}55`,
          borderWidth: 1,
        }}
        onMouseEnter={(e) => { e.currentTarget.style.background = `${OXBLOOD}18`; e.currentTarget.style.borderColor = OXBLOOD; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = `${OXBLOOD}0A`; e.currentTarget.style.borderColor = `${OXBLOOD}55`; }}
        data-testid={`compare-pricing-btn-${sku}`}
      >
        <BarChart3 size={13} strokeWidth={2.2} />
        Compare Pricing
      </button>

      {open && <ComparisonModal data={data} onClose={close} sku={sku} />}
    </>
  );
}

function ComparisonModal({ data, onClose, sku }) {
  const { product_name, bbk_vial_count, bbk_total_price, bbk_price_per_vial, competitors, updated_at } = data;
  const dateChecked = updated_at ? new Date(updated_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "";

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center overflow-y-auto py-10 px-4"
      onClick={onClose}
      data-testid="comparison-modal-backdrop"
    >
      <div className="absolute inset-0 bg-charcoal/60 backdrop-blur-sm" />
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-4xl bg-bone rounded-lg shadow-2xl overflow-hidden"
        style={{ borderColor: `${OXBLOOD}22`, borderWidth: 1 }}
        data-testid="comparison-modal"
      >
        {/* Header */}
        <div
          className="px-6 md:px-8 py-5 flex items-start justify-between border-b"
          style={{ borderColor: `${OXBLOOD}22`, background: `linear-gradient(135deg, ${OXBLOOD}08 0%, ${GOLD}0d 100%)` }}
        >
          <div>
            <p className="font-mono uppercase tracking-[0.24em] text-[10.5px] mb-1" style={{ color: OXBLOOD, opacity: 0.65 }}>
              Apples-to-apples pricing
            </p>
            <h2
              className="font-sans font-bold text-[24px] md:text-[28px] leading-none tracking-[-0.01em]"
              style={{ color: OXBLOOD, fontFamily: "Manrope, ui-sans-serif, system-ui" }}
            >
              {product_name || sku}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-sm p-1.5 hover:bg-black/5 transition-colors"
            style={{ color: OXBLOOD }}
            data-testid="comparison-modal-close"
            aria-label="Close comparison"
          >
            <X size={20} strokeWidth={1.8} />
          </button>
        </div>

        {/* Comparison table */}
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px]" data-testid="comparison-table">
            <thead>
              <tr style={{ background: `${OXBLOOD}05` }}>
                <th className="text-left px-4 md:px-6 py-3 font-mono uppercase tracking-[0.22em] text-[10px]" style={{ color: OXBLOOD, opacity: 0.65 }}>Source</th>
                <th className="text-left px-3 py-3 font-mono uppercase tracking-[0.22em] text-[10px]" style={{ color: OXBLOOD, opacity: 0.65 }}>Product</th>
                <th className="text-right px-3 py-3 font-mono uppercase tracking-[0.22em] text-[10px]" style={{ color: OXBLOOD, opacity: 0.65 }}>Vials</th>
                <th className="text-right px-3 py-3 font-mono uppercase tracking-[0.22em] text-[10px]" style={{ color: OXBLOOD, opacity: 0.65 }}>Total</th>
                <th className="text-right px-3 py-3 font-mono uppercase tracking-[0.22em] text-[10px]" style={{ color: OXBLOOD, opacity: 0.65 }}>Per vial</th>
                <th className="text-left px-3 py-3 font-mono uppercase tracking-[0.22em] text-[10px]" style={{ color: OXBLOOD, opacity: 0.65 }}>Testing</th>
                <th className="text-left px-3 py-3 font-mono uppercase tracking-[0.22em] text-[10px]" style={{ color: OXBLOOD, opacity: 0.65 }}>COA</th>
                <th className="text-left px-4 md:px-6 py-3 font-mono uppercase tracking-[0.22em] text-[10px]" style={{ color: OXBLOOD, opacity: 0.65 }}>Notes</th>
              </tr>
            </thead>
            <tbody>
              {/* BBK row — always first, highlighted subtly (NOT because cheaper) */}
              <tr className="border-t" style={{ borderColor: `${OXBLOOD}18`, background: `${GOLD}0a` }} data-testid="row-bbk">
                <td className="px-4 md:px-6 py-4">
                  <p className="font-sans font-bold text-[14px]" style={{ color: OXBLOOD, fontFamily: "Manrope, ui-sans-serif, system-ui" }}>Bulk Buy Kits</p>
                  <p className="font-mono uppercase tracking-[0.18em] text-[9.5px] mt-0.5" style={{ color: OXBLOOD, opacity: 0.55 }}>our listing</p>
                </td>
                <td className="px-3 py-4 text-[13px]" style={{ color: OXBLOOD }}>{product_name || "—"}</td>
                <td className="px-3 py-4 text-right tabular-nums text-[13px]" style={{ color: OXBLOOD }}>{bbk_vial_count}</td>
                <td className="px-3 py-4 text-right tabular-nums font-medium text-[13px]" style={{ color: OXBLOOD }}>${Number(bbk_total_price).toFixed(2)}</td>
                <td className="px-3 py-4 text-right tabular-nums font-medium text-[13px]" style={{ color: OXBLOOD }}>${Number(bbk_price_per_vial).toFixed(2)}</td>
                <td className="px-3 py-4 text-[12.5px]" style={{ color: OXBLOOD }}>Independent 3rd-party</td>
                <td className="px-3 py-4 text-[12.5px]" style={{ color: OXBLOOD }}>Available</td>
                <td className="px-4 md:px-6 py-4 text-[12px] max-w-[240px]" style={{ color: OXBLOOD, opacity: 0.75 }}>3-vial kit, batch-tested, COA on request</td>
              </tr>
              {competitors.map((c) => (
                <tr key={c.id || c.name} className="border-t" style={{ borderColor: `${OXBLOOD}18` }} data-testid={`row-comp-${(c.name || "").toLowerCase().replace(/\s+/g,'-')}`}>
                  <td className="px-4 md:px-6 py-4">
                    {c.website_url ? (
                      <a
                        href={c.website_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-sans font-bold text-[14px] inline-flex items-center gap-1 hover:underline"
                        style={{ color: OXBLOOD, fontFamily: "Manrope, ui-sans-serif, system-ui" }}
                      >
                        {c.name}
                        <ExternalLink size={11} strokeWidth={1.8} className="opacity-60" />
                      </a>
                    ) : (
                      <p className="font-sans font-bold text-[14px]" style={{ color: OXBLOOD, fontFamily: "Manrope, ui-sans-serif, system-ui" }}>{c.name}</p>
                    )}
                    {c.date_checked && (
                      <p className="font-mono uppercase tracking-[0.18em] text-[9.5px] mt-0.5" style={{ color: OXBLOOD, opacity: 0.5 }}>
                        Checked {c.date_checked}
                      </p>
                    )}
                  </td>
                  <td className="px-3 py-4 text-[13px]" style={{ color: OXBLOOD, opacity: 0.9 }}>
                    {c.product_name}
                    {c.strength ? <span className="opacity-60"> · {c.strength}</span> : null}
                  </td>
                  <td className="px-3 py-4 text-right tabular-nums text-[13px]" style={{ color: OXBLOOD, opacity: 0.9 }}>{c.vial_count}</td>
                  <td className="px-3 py-4 text-right tabular-nums text-[13px]" style={{ color: OXBLOOD, opacity: 0.9 }}>${Number(c.total_price).toFixed(2)}</td>
                  <td className="px-3 py-4 text-right tabular-nums text-[13px]" style={{ color: OXBLOOD, opacity: 0.9 }}>${Number(c.price_per_vial).toFixed(2)}</td>
                  <td className="px-3 py-4 text-[12.5px]" style={{ color: OXBLOOD, opacity: 0.9 }}>{c.testing_status || "—"}</td>
                  <td className="px-3 py-4 text-[12.5px]" style={{ color: OXBLOOD, opacity: 0.9 }}>{c.coa_available ? "Available" : "Not listed"}</td>
                  <td className="px-4 md:px-6 py-4 text-[12px] max-w-[240px]" style={{ color: OXBLOOD, opacity: 0.7 }}>
                    {[c.comparison_notes, c.shipping_notes, c.source_notes].filter(Boolean).join(" · ") || "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Footer notes */}
        <div className="px-4 md:px-6 py-5 border-t space-y-1.5" style={{ borderColor: `${OXBLOOD}22`, background: `${OXBLOOD}03` }}>
          <p className="text-[12px] leading-relaxed" style={{ color: OXBLOOD, opacity: 0.8 }}>
            Competitor pricing is manually reviewed and selected for comparable
            product and testing standards. Prices may change.
          </p>
          {dateChecked && (
            <p className="font-mono uppercase tracking-[0.22em] text-[10px]" style={{ color: OXBLOOD, opacity: 0.55 }}>
              Price checked: {dateChecked}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
